import type { ShallowRef, VNode } from 'vue'
import { computed, Fragment, watchPostEffect } from 'vue'

type VNodesProps<T extends object> = Array<VNode & { props: T }>
type SlotFn = (props?: any) => VNode[] | VNode | undefined

function getTopLevelSlotNodes(slotFn?: SlotFn): VNode[] {
  const nodes = slotFn?.()

  if (!nodes)
    return []

  const topLevelNodes = Array.isArray(nodes) ? nodes : [nodes]
  const result: VNode[] = []

  const unwrapTopLevelFragments = (vnodes: VNode[]): void => {
    vnodes.forEach((vnode) => {
      if (vnode.type === Fragment && Array.isArray(vnode.children)) {
        unwrapTopLevelFragments(vnode.children as VNode[])
      }
      else {
        result.push(vnode)
      }
    })
  }

  unwrapTopLevelFragments(topLevelNodes)
  return result
}

/**
 * Flattens slot children and keeps them in sync reactively.
 *
 * @param slotFn The slot function (e.g., slots.default)
 */
export function useFlattenedSlot<T extends object>(slotFn?: SlotFn): ShallowRef<VNodesProps<T>> {
  // Flatten VNodes recursively (handles Fragments)
  function flatten(vnodes: VNode[]): VNodesProps<T> {
    const result: VNode[] = []

    const walk = (nodes: VNode[] | VNode | undefined): void => {
      if (!nodes)
        return

      if (Array.isArray(nodes)) {
        nodes.forEach(walk)
      }
      else if (nodes.type === Fragment || Array.isArray(nodes.children)) {
        walk(nodes.children as VNode[])
      }
      else {
        result.push(nodes)
      }
    }

    walk(vnodes)
    return result as VNodesProps<T>
  }

  return computed(() => flatten(getTopLevelSlotNodes(slotFn)))
}

/**
 * Returns a computed array of top-level VNodes from the provided slot function
 *
 * @param slotFn The slot function (e.g., slots.default)
 */
export function useTopLevelSlots(slotFn?: SlotFn): ShallowRef<VNodesProps<any>> {
  return computed(() => getTopLevelSlotNodes(slotFn) as VNodesProps<any>)
}

/**
 * Checks whether all of the provided VNodes are of the same type. Throws an
 * error if not. This function should be used in components that only allow a
 * single type of component
 *
 * @param vnodes Array of VNodes to check
 * @param name Expected name of the components
 */
export function enforceSlotType(vnodes: ShallowRef<VNodesProps<any>>, name: string): void {
  watchPostEffect(() => {
    // @ts-expect-error Accessing internals
    if (vnodes.value.some(item => item.type.__name !== name)) {
      throw new TypeError(`You can only pass \`<${name} />\` components as children.`)
    }
  })
}
